import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  User,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge, getStatusVariant } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { formatDate, formatRelativeTime, getInitials } from '@/lib/utils';
import toast from 'react-hot-toast';

interface KYCApplication {
  id: string;
  userId: string;
  userName: string;
  phone: string;
  fullName: string;
  dateOfBirth: string;
  aadhaarNumber: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected';
  submittedAt: string;
  documents: {
    aadhaarFront: string;
    aadhaarBack: string;
    selfie: string;
  };
}

const mockKYCs: KYCApplication[] = [];

export default function KYCList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedKYC, setSelectedKYC] = useState<KYCApplication | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const itemsPerPage = 10;

  const filteredKYCs = mockKYCs.filter((kyc) => {
    const matchesSearch =
      kyc.userName.toLowerCase().includes(search.toLowerCase()) ||
      kyc.phone.includes(search);
    const matchesStatus = statusFilter === 'all' || kyc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredKYCs.length / itemsPerPage);
  const paginatedKYCs = filteredKYCs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const pendingCount = mockKYCs.filter((k) => k.status === 'pending').length;
  const reviewCount = mockKYCs.filter((k) => k.status === 'under_review').length;

  const handleApprove = () => {
    toast.success('KYC application approved successfully');
    setShowReviewModal(false);
    setSelectedKYC(null);
  };

  const handleReject = () => {
    if (!rejectReason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    toast.success('KYC application rejected');
    setShowReviewModal(false);
    setSelectedKYC(null);
    setRejectReason('');
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold">KYC Applications</h1>
          <p className="text-slate-400 mt-1">Review and manage user KYC submissions</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/kyc/settings')}>
            Settings
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total Applications" value={mockKYCs.length} />
        <StatCard label="Pending Review" value={pendingCount} color="amber" urgent />
        <StatCard label="Under Review" value={reviewCount} color="blue" />
        <StatCard label="Approved Today" value={5} color="green" />
      </div>

      {/* Filters */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </Card>

      {/* KYC List */}
      <Card variant="glass" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Applicant
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Documents
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Aadhaar
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Submitted
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {paginatedKYCs.map((kyc) => (
                <tr key={kyc.id} className="hover:bg-slate-800/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                        {getInitials(kyc.userName)}
                      </div>
                      <div>
                        <p className="font-medium">{kyc.fullName}</p>
                        <p className="text-sm text-slate-500">{kyc.phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm">{kyc.aadhaarNumber}</span>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={getStatusVariant(kyc.status)}>
                      {kyc.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm">{formatDate(kyc.submittedAt)}</p>
                      <p className="text-xs text-slate-500">{formatRelativeTime(kyc.submittedAt)}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedKYC(kyc);
                          setShowReviewModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
          <p className="text-sm text-slate-400">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, filteredKYCs.length)} of {filteredKYCs.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>KYC Review - {selectedKYC?.fullName}</DialogTitle>
            <DialogDescription>
              Review the submitted documents and verify user identity
            </DialogDescription>
          </DialogHeader>

          {selectedKYC && (
            <div className="py-4 space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Full Name</p>
                  <p className="font-medium">{selectedKYC.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Date of Birth</p>
                  <p className="font-medium">{selectedKYC.dateOfBirth}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Phone</p>
                  <p className="font-medium">{selectedKYC.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Aadhaar Number</p>
                  <p className="font-medium font-mono">{selectedKYC.aadhaarNumber}</p>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="font-semibold mb-3">Submitted Documents</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">Aadhaar Front</p>
                    <div className="aspect-[4/3] rounded-lg bg-slate-800 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-slate-500" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">Aadhaar Back</p>
                    <div className="aspect-[4/3] rounded-lg bg-slate-800 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-slate-500" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">Selfie</p>
                    <div className="aspect-[4/3] rounded-lg bg-slate-800 flex items-center justify-center">
                      <User className="w-8 h-8 text-slate-500" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Rejection Reason */}
              {selectedKYC.status !== 'approved' && (
                <div>
                  <label className="text-sm font-medium text-slate-300">Rejection Reason (if rejecting)</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="mt-2 w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                    rows={2}
                    placeholder="Enter reason for rejection..."
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button variant="success" onClick={handleApprove}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function StatCard({
  label,
  value,
  color,
  urgent,
}: {
  label: string;
  value: number;
  color?: string;
  urgent?: boolean;
}) {
  const colorClasses: Record<string, string> = {
    green: 'text-emerald-400',
    amber: 'text-amber-400',
    blue: 'text-blue-400',
  };

  return (
    <Card variant="glass" className="p-4 relative">
      {urgent && value > 0 && (
        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
      )}
      <p className="text-sm text-slate-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color ? colorClasses[color] : ''}`}>{value}</p>
    </Card>
  );
}
